import React from 'react';
import { Table, Tag, Button, Empty, Tooltip } from 'antd';
import { EyeOutlined, PhoneOutlined, UserOutlined } from '@ant-design/icons';
import { Fornecedor } from '../../../../types/dashboard/fornecedores.types';
import type { ColumnsType } from 'antd/es/table';

interface FornecedoresTableProps {
  data: Fornecedor[];
  total: number;
  current: number;
  pageSize: number;
  loading: boolean;
  onPaginationChange: (page: number, pageSize?: number) => void;
  onViewFornecedor?: (fornecedorId: string) => void;
}

const FornecedoresTable: React.FC<FornecedoresTableProps> = ({
  data,
  total,
  current,
  pageSize,
  loading,
  onPaginationChange,
  onViewFornecedor
}) => {
  // Configuração das colunas da tabela com tipagem correta
  const columns: ColumnsType<Fornecedor> = [
    {
      title: 'Razão Social',
      dataIndex: 'razaoSocial',
      key: 'razaoSocial',
      render: (text: string, record: Fornecedor) => (
        <div>
          <div>{text || 'Nome não disponível'}</div>
          {record.representante && (
            <div style={{ fontSize: '12px', color: '#888' }}>
              <UserOutlined style={{ marginRight: 4 }} />
              {record.representante}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj',
      key: 'cnpj',
      render: (text: string) => {
        if (!text) return '-';
        // Verificar se o CNPJ tem 14 dígitos antes de tentar formatá-lo
        if (text.length === 14 && /^\d+$/.test(text)) {
          try {
            return text.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
          } catch (e) {
            return text;
          }
        }
        return text;
      }
    },
    {
      title: 'Email / Telefone',
      key: 'contato',
      render: (text: string, record: Fornecedor) => (
        <div>
          <div>{record.email || '-'}</div>
          {record.telefone && (
            <div style={{ fontSize: '12px' }}>
              <PhoneOutlined style={{ marginRight: 4 }} />
              {record.telefone}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Área',
      dataIndex: 'area',
      key: 'area',
      render: (text: string) => text || '-'
    },
    {
      title: 'Último questionário',
      dataIndex: 'ultimoQuestionario',
      key: 'ultimoQuestionario',
      render: (ultimoQuestionario: any) => {
        if (!ultimoQuestionario || !ultimoQuestionario.status) return <Tag color="default">Não definido</Tag>;
        
        // Definir a cor do status
        let color = 'default';
        let dotColor = '#D9D9D9';
        
        switch (ultimoQuestionario.status) {
          case 'Pendente':
          case 'pendente':
            color = 'gold';
            dotColor = '#FFC107';
            break;
          case 'Enviado':
          case 'enviado':
            color = 'green';
            dotColor = '#52C41A';
            break;
          case 'Respondido':
          case 'respondido':
            color = 'blue';
            dotColor = '#1890FF';
            break;
          case 'Em análise':
          case 'em_analise':
            color = 'purple';
            dotColor = '#722ED1';
            break;
          case 'Não avaliado':
            color = 'default';
            dotColor = '#D9D9D9';
            break;
          default:
            color = 'default';
            dotColor = '#D9D9D9';
        }
        
        // Converter para formato amigável (primeira letra maiúscula)
        const formattedStatus = formatStatus(ultimoQuestionario.status);
        
        return (
          <Tag color={color} style={{ borderRadius: 10 }}>
            <span style={{ 
              display: 'inline-block', 
              width: 8, 
              height: 8, 
              backgroundColor: dotColor, 
              borderRadius: '50%',
              marginRight: 6 
            }} />
            {formattedStatus}
          </Tag>
        );
      }
    },
    {
      title: 'Status Ação',
      dataIndex: 'status_acao',
      key: 'statusAcao',
      render: (status: string) => {
        if (!status || status === 'Não avaliado') {
          return <Tag color="default">Não avaliado</Tag>;
        }
        
        let color = 'default';
        switch (status.toLowerCase()) {
          case 'continuar':
            color = 'green';
            break;
          case 'descontinuar':
            color = 'red';
            break;
          case 'continuarcontrato':
            color = 'blue';
            break;
        }
        
        return <Tag color={color}>{formatStatusAcao(status)}</Tag>;
      }
    },
    {
      title: 'Ações',
      key: 'acoes',
      render: (_: any, record: Fornecedor) => (
        <Button 
          type="default" 
          icon={<EyeOutlined />} 
          size="small"
          onClick={() => {
            if (onViewFornecedor && record.id) {
              onViewFornecedor(record.id);
            } else {
              console.log('Visualizar fornecedor', record);
            }
          }}
          disabled={!record.id}
        >
          Ver
        </Button>
      ),
    },
  ];

  // Formatação do status de ação para exibição
  const formatStatusAcao = (status: string) => {
    switch (status.toLowerCase()) {
      case 'continuar':
        return 'Continuar';
      case 'descontinuar':
        return 'Descontinuar';
      case 'continuarcontrato':
        return 'Continuar (contrato)';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };
  
  // Formatar status com primeira letra maiúscula e underscores substituídos por espaços
  const formatStatus = (status: string) => {
    if (!status) return 'Não definido';
    
    // Substituir underscores por espaços
    let formattedStatus = status.replace(/_/g, ' ');
    
    // Converter primeira letra para maiúscula
    return formattedStatus.charAt(0).toUpperCase() + formattedStatus.slice(1);
  };

  // Verificar se temos dados
  const hasFornecedores = Array.isArray(data) && data.length > 0;

  return (
    <Table
      dataSource={hasFornecedores ? data : []}
      columns={columns}
      rowKey="id"
      pagination={{
        total,
        current,
        pageSize,
        onChange: onPaginationChange,
        showSizeChanger: true,
        showTotal: (total) => `Total de ${total} fornecedores`,
      }}
      loading={loading}
      locale={{ 
        emptyText: <Empty description="Nenhum fornecedor encontrado" /> 
      }}
      style={{ backgroundColor: '#fff', borderRadius: 8 }}
      scroll={{ x: 1000 }} // Adicionar rolagem horizontal para responsividade
    />
  );
};

export default FornecedoresTable;